/**
 * Publication filter pill row.
 *
 * Sticky across the Notifications Queue and Social Drafts pages — the
 * pill selection persists in localStorage and synchronises across tabs
 * in the same window via a custom event (same pattern as
 * EntityGroupFilter).
 *
 * Pills are sourced from `listEgressTargets()` filtered to
 * `is_active === true && target_type === '11ty_git'` — so flipping a
 * publication's `is_active` flag in the DB hides it here automatically.
 * The Pick is inactive today so doesn't appear; if it ever flips on,
 * it surfaces without code change.
 *
 * Display labels are derived from `target_name` via a small mapping so
 * the operator sees "Bell Wire" / "Orecast" / "The Market Floor" rather
 * than the raw slug. Unknown slugs fall back to title-case.
 */

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listEgressTargets } from '../lib/api/egress.js';

const STORAGE_KEY = 'assayer.publicationPill';
const CHANGE_EVENT = 'assayer:publication-changed';
const TARGET_TYPE_FOR_PUBLICATIONS = '11ty_git';

/** The current pill is the `target_name` slug, or 'all'. */
export type PublicationPill = string;

/** Friendly display labels keyed by target_name. */
const LABEL_OVERRIDES: Record<string, string> = {
  bellwire: 'Bell Wire',
  orecast: 'Orecast',
  marketfloor: 'The Market Floor',
  thepick: 'The Pick',
};

function prettyLabel(slug: string): string {
  if (LABEL_OVERRIDES[slug]) return LABEL_OVERRIDES[slug];
  // Fallback: title-case the slug
  return slug
    .split(/[-_]/)
    .map((p) => (p.length ? p[0].toUpperCase() + p.slice(1) : p))
    .join(' ');
}

function readStored(): PublicationPill {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return raw;
  } catch {
    // localStorage unavailable (private browsing, etc.) — fall through.
  }
  return 'all';
}

/**
 * Hook returning the current pill + setter + resolved target_id.
 *
 * `targetId` is `null` when the pill is 'all' OR when the targets list
 * hasn't loaded yet (or the stored slug doesn't match a current target).
 * Callers should pass `targetId ?? undefined` to list-endpoint query
 * params so 'all' = no filter.
 */
export function usePublicationPill(): {
  pill: PublicationPill;
  setPill: (next: PublicationPill) => void;
  targetId: string | null;
} {
  const [pill, setPillState] = useState<PublicationPill>(readStored);

  const { data: targetsData } = useQuery({
    queryKey: ['egress-targets-for-publication-filter'],
    queryFn: listEgressTargets,
    // Cached aggressively — the list of active publications changes rarely.
    staleTime: 5 * 60 * 1000,
  });

  const activePublications = (targetsData?.items ?? []).filter(
    (t) => t.is_active && t.target_type === TARGET_TYPE_FOR_PUBLICATIONS,
  );

  const targetId =
    pill === 'all'
      ? null
      : activePublications.find((t) => t.target_name === pill)?.target_id ?? null;

  const setPill = (next: PublicationPill) => {
    setPillState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // ignore — state still updates in-tab
    }
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: next }));
  };

  useEffect(() => {
    const onChange = (e: Event) => {
      const next = (e as CustomEvent<PublicationPill>).detail;
      if (next && next !== pill) setPillState(next);
    };
    window.addEventListener(CHANGE_EVENT, onChange);
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && e.newValue) {
        setPillState(e.newValue);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(CHANGE_EVENT, onChange);
      window.removeEventListener('storage', onStorage);
    };
  }, [pill]);

  return { pill, setPill, targetId };
}

/**
 * Pill row UI. Drop into list pages alongside the EntityGroupFilter.
 */
export function PublicationFilter() {
  const { pill, setPill } = usePublicationPill();
  const { data: targetsData, isLoading } = useQuery({
    queryKey: ['egress-targets-for-publication-filter'],
    queryFn: listEgressTargets,
    staleTime: 5 * 60 * 1000,
  });

  const activePublications = (targetsData?.items ?? [])
    .filter((t) => t.is_active && t.target_type === TARGET_TYPE_FOR_PUBLICATIONS)
    .sort((a, b) => a.target_name.localeCompare(b.target_name));

  // Hide the row entirely while loading to avoid layout jitter.
  if (isLoading || activePublications.length === 0) {
    return null;
  }

  return (
    <div
      className="flex items-center gap-1.5 flex-wrap"
      role="tablist"
      aria-label="Publication filter"
    >
      <button
        type="button"
        role="tab"
        aria-selected={pill === 'all'}
        onClick={() => setPill('all')}
        title="Show drafts across every publication"
        className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
          pill === 'all'
            ? 'bg-primary-600 text-white'
            : 'bg-surface text-grey-700 border border-grey-300 hover:bg-grey-50'
        }`}
      >
        All publications
      </button>
      {activePublications.map((p) => {
        const isActive = pill === p.target_name;
        return (
          <button
            key={p.target_id}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => setPill(p.target_name)}
            title={`Filter drafts to ${prettyLabel(p.target_name)} only`}
            className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
              isActive
                ? 'bg-primary-600 text-white'
                : 'bg-surface text-grey-700 border border-grey-300 hover:bg-grey-50'
            }`}
          >
            {prettyLabel(p.target_name)}
          </button>
        );
      })}
    </div>
  );
}
